/* AI Quality Program — real-time monitoring board.
   Ported from the design bundle's monitoring.jsx. A live status bar with a
   rotating feed, plus six signal cards each with a gentle sparkline.
   Self-contained: injects its own aqp-* styles. Green = "nominal / healthy". */

import React, { useState, useEffect, useMemo } from 'react';

const SPARKS = {
  reject:  [0.52, 0.48, 0.55, 0.5, 0.46, 0.58, 0.62, 0.54, 0.49, 0.52, 0.47, 0.5],
  cluster: [0.5, 0.46, 0.48, 0.45, 0.5, 0.47, 0.52, 0.66, 0.58, 0.5, 0.48, 0.51],
  cost:    [0.6, 0.58, 0.55, 0.57, 0.53, 0.52, 0.5, 0.51, 0.48, 0.5, 0.49, 0.47],
  budget:  [0.3, 0.34, 0.38, 0.42, 0.46, 0.49, 0.53, 0.56, 0.6, 0.63, 0.66, 0.69],
  comply:  [0.5, 0.52, 0.5, 0.51, 0.49, 0.5, 0.52, 0.5, 0.51, 0.5, 0.49, 0.5],
};

function Sparkline({ data, accent = '#6366F1', watch = false }) {
  const W = 240, H = 64, P = 6;
  const max = Math.max(...data), min = Math.min(...data);
  const span = Math.max(0.0001, max - min);
  const pts = data.map((v, i) => {
    const x = P + (i / (data.length - 1)) * (W - P * 2);
    const y = H - P - ((v - min) / span) * (H - P * 2);
    return [x, y];
  });
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const area = `${d} L${pts[pts.length - 1][0].toFixed(1)} ${H} L${pts[0][0].toFixed(1)} ${H} Z`;
  const last = pts[pts.length - 1];
  const gid = useMemo(() => 'sg' + Math.random().toString(36).slice(2, 8), []);
  const stroke = watch ? '#FFC27D' : accent;
  return (
    <svg className="aqp-signal__spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.28" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={d} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.92" />
      <circle cx={last[0]} cy={last[1]} r="3.4" fill={stroke}>
        <animate attributeName="r" values="3.4;5.2;3.4" dur="2.4s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;0.45;1" dur="2.4s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

const SIGNALS = [
  {
    no: '01',
    title: 'Reject & repair-rate spikes',
    desc: 'By facility, fabricator, or welding team. A sudden rise is surfaced before it spreads.',
    spark: 'reject',
    status: 'nominal',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l5-6 4 4 5-7 4 5" /><path d="M3 21h18" /></svg>),
  },
  {
    no: '02',
    hero: true,
    title: 'Decision-pattern monitoring',
    desc: 'Watching crews for over-calling that drives unnecessary rejects, so it can be caught and corrected.',
    stat: '20',
    statSym: '%',
    statCap: 'Up to this much fewer unnecessary rejects on projects we have run',
    status: 'nominal',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 20a8 8 0 0 1 16 0" /></svg>),
  },
  {
    no: '03',
    title: 'Indication & defect-type concentration',
    desc: 'A sudden clustering of one indication type, flagged at the first sign so an upstream process problem can be found early.',
    spark: 'cluster',
    status: 'watch',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="7" cy="8" r="2" /><circle cx="11" cy="12" r="2" /><circle cx="9" cy="16" r="2" /><circle cx="16" cy="10" r="2" /><circle cx="17" cy="16" r="2" /></svg>),
  },
  {
    no: '04',
    central: true,
    title: 'Cost per inspection',
    desc: 'A central signal. A rising cost per inspection points to resource and utilization issues, and more.',
    spark: 'cost',
    status: 'nominal',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.5a2.5 2 0 0 1 5 0c0 2-5 1-5 3a2.5 2 0 0 0 5 0" /></svg>),
  },
  {
    no: '05',
    title: 'Budget trajectory',
    desc: 'Burn tracked against the project budget, with a proactive note when it is trending to run over.',
    spark: 'budget',
    status: 'watch',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M7 15l4-4 3 2 5-7" /><path d="M18 6h2v2" /></svg>),
  },
  {
    no: '06',
    title: 'Compliance gaps',
    desc: 'Missing records or documents, and certifications approaching expiry, flagged early.',
    spark: 'comply',
    status: 'nominal',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4" /><path d="M12 3l8 3v5c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>),
  },
];

const TICKER = [
  { kind: 'nominal', label: 'Nominal', text: 'All monitored signals within expected range' },
  { kind: 'watch', label: 'Watch', text: 'Reject rate trending up on one welding team. Raised for review the same day.' },
  { kind: 'nominal', label: 'Nominal', text: 'Cost per inspection steady week over week' },
  { kind: 'watch', label: 'Watch', text: 'Indication clustering detected upstream. Flagged at the first sign.' },
  { kind: 'nominal', label: 'Nominal', text: 'Documentation coverage complete. No certifications expiring this period.' },
];

function MonitorBar() {
  const [idx, setIdx] = useState(0);
  const [on, setOn] = useState(true);
  const [secs, setSecs] = useState(2);

  useEffect(() => {
    const rotate = setInterval(() => {
      setOn(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % TICKER.length);
        setOn(true);
      }, 420);
    }, 4200);
    return () => clearInterval(rotate);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSecs((s) => (s >= 9 ? 1 : s + 1)), 1000);
    return () => clearInterval(t);
  }, []);

  const line = TICKER[idx];
  return (
    <div className="aqp-monitor-bar">
      <span className="aqp-monitor-bar__live"><span className="aqp-monitor-bar__dot" />Live monitoring</span>
      <span className="aqp-monitor-bar__sep" />
      <div className="aqp-monitor-bar__ticker">
        <div className={'aqp-ticker-line' + (on ? ' is-on' : '')}>
          <span className={'pill ' + line.kind}>{line.label}</span>
          <span>{line.text}</span>
        </div>
      </div>
      <span className="aqp-monitor-bar__synced">synced {secs}s ago</span>
    </div>
  );
}

function SignalCard({ s }) {
  const accent = s.central ? '#8B5CF6' : '#6366F1';
  const cls = [
    'aqp-signal',
    s.hero ? 'aqp-signal--hero' : '',
    s.central ? 'aqp-signal--central' : '',
  ].join(' ');
  return (
    <article className={cls}>
      <div className="aqp-signal__top">
        <span className="aqp-signal__no">{s.no}</span>
        <span className={'aqp-signal__status ' + s.status}>
          <span className="sd" />
          {s.status === 'watch' ? 'Watching' : 'Nominal'}
        </span>
      </div>
      <div className="aqp-signal__title">{s.title}</div>
      <p className="aqp-signal__desc">{s.desc}</p>
      {s.hero ? (
        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'flex-end', gap: 14 }}>
          <div className="aqp-signal__stat">{s.stat}<span>{s.statSym}</span></div>
          <div className="aqp-signal__statcap">{s.statCap}</div>
        </div>
      ) : (
        <>
          {s.central && <div className="aqp-signal__breakdown"><em>Central signal</em> · points back to utilization</div>}
          <Sparkline data={SPARKS[s.spark]} accent={accent} watch={s.status === 'watch'} />
        </>
      )}
    </article>
  );
}

function MonitoringBoard() {
  return (
    <div className="aqp-mb">
      <MonitorBar />
      <div className="aqp-signals">
        {SIGNALS.map((s) => <SignalCard key={s.no} s={s} />)}
      </div>
      <p className="aqp-monitor-foot">
        These six run by default as a base package. During setup we expand the list with custom signals for your
        project. <strong>Whatever you want watched, we add it.</strong>
      </p>
      <style dangerouslySetInnerHTML={{ __html: MB_CSS }} />
    </div>
  );
}

const MB_CSS = `
  .aqp-mb { --g-nom: #19D6A1; }
  .aqp-monitor-bar {
    display: flex; align-items: center; gap: 18px; margin-bottom: 18px;
    padding: 14px 20px; background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08); border-radius: var(--orbix-r-md);
    position: relative; overflow: hidden;
  }
  .aqp-monitor-bar__live {
    display: inline-flex; align-items: center; gap: 9px;
    font-family: var(--orbix-font-ui); font-weight: 700; font-size: 11px;
    letter-spacing: 0.16em; text-transform: uppercase; color: var(--g-nom); white-space: nowrap;
  }
  .aqp-monitor-bar__dot { width: 9px; height: 9px; border-radius: 50%; background: var(--g-nom); box-shadow: 0 0 12px var(--g-nom); animation: aqp-blink 1.8s ease-in-out infinite; }
  .aqp-monitor-bar__sep { width: 1px; height: 18px; background: rgba(255,255,255,0.14); }
  .aqp-monitor-bar__ticker { flex: 1; min-width: 0; overflow: hidden; height: 20px; position: relative; }
  .aqp-ticker-line {
    position: absolute; inset: 0; display: flex; align-items: center; gap: 10px;
    font-family: var(--orbix-font-ui); font-size: 13px; color: var(--orbix-fg-on-dark-dim);
    letter-spacing: 0.02em; opacity: 0; transform: translateY(6px);
    transition: opacity 420ms var(--orbix-ease), transform 420ms var(--orbix-ease);
  }
  .aqp-ticker-line.is-on { opacity: 1; transform: translateY(0); }
  .aqp-ticker-line .pill {
    font-family: var(--orbix-font-ui); font-weight: 700; font-size: 10px;
    letter-spacing: 0.12em; text-transform: uppercase; padding: 3px 9px; border-radius: var(--orbix-r-pill); white-space: nowrap;
  }
  .aqp-ticker-line .pill.nominal { color: var(--g-nom); background: rgba(25,214,161,0.12); border: 1px solid rgba(25,214,161,0.32); }
  .aqp-ticker-line .pill.watch { color: #FFC27D; background: rgba(255,194,125,0.12); border: 1px solid rgba(255,194,125,0.34); }
  .aqp-monitor-bar__synced { font-family: var(--orbix-font-ui); font-size: 11px; color: var(--orbix-fg-on-dark-mute); white-space: nowrap; font-variant-numeric: tabular-nums; letter-spacing: 0.04em; }

  .aqp-signals { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .aqp-signal {
    position: relative; background: rgba(255,255,255,0.035);
    border: 1px solid rgba(255,255,255,0.08); border-radius: var(--orbix-r-lg);
    padding: 20px 22px 18px; display: flex; flex-direction: column; gap: 12px; min-height: 188px;
    transition: border-color var(--orbix-d-md) var(--orbix-ease), background var(--orbix-d-md) var(--orbix-ease);
  }
  .aqp-signal:hover { border-color: rgba(129,107,255,0.4); background: rgba(255,255,255,0.05); }
  .aqp-signal__top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
  .aqp-signal__no { font-family: ui-monospace, "JetBrains Mono", monospace; font-size: 11px; font-weight: 500; letter-spacing: 0.1em; color: rgba(255,255,255,0.3); }
  .aqp-signal__status {
    font-family: var(--orbix-font-ui); font-weight: 700; font-size: 9.5px;
    letter-spacing: 0.12em; text-transform: uppercase; padding: 4px 9px; border-radius: var(--orbix-r-pill);
    display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
  }
  .aqp-signal__status .sd { width: 6px; height: 6px; border-radius: 50%; }
  .aqp-signal__status.nominal { color: var(--g-nom); background: rgba(25,214,161,0.10); border: 1px solid rgba(25,214,161,0.3); }
  .aqp-signal__status.nominal .sd { background: var(--g-nom); box-shadow: 0 0 8px var(--g-nom); }
  .aqp-signal__status.watch { color: #FFC27D; background: rgba(255,194,125,0.10); border: 1px solid rgba(255,194,125,0.32); }
  .aqp-signal__status.watch .sd { background: #FFC27D; box-shadow: 0 0 8px #FFC27D; animation: aqp-blink 1.4s ease-in-out infinite; }
  .aqp-signal__title { font-family: var(--orbix-font-display); font-weight: 700; font-size: clamp(16px, 1.25vw, 18px); line-height: 1.2; letter-spacing: -0.005em; color: var(--orbix-fg-on-dark); }
  .aqp-signal__desc { font-family: var(--orbix-font-ui); font-size: 12.5px; line-height: 1.5; color: var(--orbix-fg-on-dark-mute); margin: 0; }
  .aqp-signal__spark { margin-top: auto; height: 34px; width: 100%; display: block; }
  .aqp-signal__breakdown { font-family: var(--orbix-font-ui); font-size: 11px; color: var(--orbix-fg-on-dark-mute); letter-spacing: 0.04em; }
  .aqp-signal__breakdown em { font-style: normal; color: var(--orbix-fg-on-dark-dim); font-weight: 600; }

  .aqp-signal--hero { background: linear-gradient(135deg, rgba(99,102,241,0.16), rgba(217,70,239,0.06)); border-color: rgba(129,107,255,0.4); }
  .aqp-signal--central { border-color: rgba(139,92,246,0.34); }
  .aqp-signal__stat {
    font-family: var(--orbix-font-ui); font-weight: 800; font-size: clamp(30px, 2.8vw, 40px);
    line-height: 1; letter-spacing: -0.02em; font-variant-numeric: tabular-nums;
    background: var(--orbix-brand-gradient); -webkit-background-clip: text; background-clip: text; color: transparent;
  }
  .aqp-signal__stat span { font-size: 0.5em; vertical-align: super; margin-left: 1px; }
  .aqp-signal__statcap { font-family: var(--orbix-font-ui); font-size: 11px; color: var(--orbix-fg-on-dark-dim); letter-spacing: 0.04em; line-height: 1.4; }

  .aqp-monitor-foot { margin: 20px 0 0; font-family: var(--orbix-font-display); font-size: clamp(14px, 1.1vw, 17px); line-height: 1.5; color: var(--orbix-fg-on-dark-dim); max-width: 1000px; text-wrap: pretty; }
  .aqp-monitor-foot strong { color: var(--orbix-fg-on-dark); font-weight: 700; }

  @media (max-width: 1100px) { .aqp-signals { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 680px) { .aqp-signals { grid-template-columns: 1fr; } }
`;

export default MonitoringBoard;
